import React, { useState } from 'react';
import { Search, X, Plus } from 'lucide-react';
import { inputCls, LockTip } from './FormKit';
import { sectionTitle } from '../workflow/constants';

// Building blocks of a settings page (Overdue Rules & Reminder, Training
// Follow-up Reminder): a card of rows, a switch, the staff picker and the
// daily send-time controls. One copy, so every settings page reads the same.

// One settings group: a grey header strip (title, one-line hint, and whatever
// belongs at the right — the tab's Save, a category's chips) over rows
// separated by hairlines. The strip is the same one the New pages'
// FormSection wears, so a settings card and a form step read as the same
// kind of block. slate-100, not slate-50: the page itself is gray-50.
export const SettingsCard = ({ title, hint = null, aside = null, children }) => (
  <section className="bg-white rounded-lg border border-slate-200">
    {/* The title block grows into all the width the aside leaves, so a long
        hint wraps inside it instead of pushing the aside onto its own line. */}
    <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-2 px-5 py-3 bg-slate-100 border-b border-slate-200 rounded-t-[7px]">
      <div className="min-w-0 flex-1 basis-[20rem]">
        <h2 className={sectionTitle}>{title}</h2>
        {hint && <p className="text-xs text-slate-500 mt-0.5 break-words">{hint}</p>}
      </div>
      {aside && <div className="shrink-0 ml-auto flex flex-wrap items-center gap-x-4 gap-y-1">{aside}</div>}
    </div>
    <div className="divide-y divide-slate-200">{children}</div>
  </section>
);

// One row of a settings card: what the setting is (left rail — title and the
// rule in a sentence) and its controls (right). The rail carries the
// explanation once, so no control needs its own paragraph.
export const SettingsRow = ({ title, description = null, children }) => (
  <div className="grid grid-cols-1 md:grid-cols-[14rem_minmax(0,1fr)] gap-x-8 gap-y-3 px-5 py-5">
    <div className="min-w-0">
      <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
      {description && <p className="text-xs text-slate-500 mt-1 leading-relaxed">{description}</p>}
    </div>
    <div className="min-w-0 space-y-4">{children}</div>
  </div>
);

// Chips in a card's header strip (on slate-100, so Off is white).
const chipBase = 'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium';
export const chipOn = `${chipBase} bg-blue-50 text-blue-700`;
export const chipOff = `${chipBase} bg-white border border-slate-200 text-slate-500`;
export const chipWarn = `${chipBase} bg-amber-50 text-amber-700`;

export const Toggle = ({ checked, onChange, label }) => (
  <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none">
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${checked ? 'bg-blue-600' : 'bg-slate-300'}`}
    >
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${checked ? 'translate-x-4' : 'translate-x-1'}`} />
    </button>
    {label}
  </label>
);

export const CheckList = ({ options, selected, onToggle }) => (
  <div className="max-w-md border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-40 overflow-y-auto">
    {options.length === 0 ? (
      <p className="px-3 py-2 text-sm text-slate-500">No options</p>
    ) : options.map(o => (
      <label key={o} className="flex items-center gap-2 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 cursor-pointer">
        <input type="checkbox" checked={selected.includes(o)} onChange={() => onToggle(o)} />
        {o}
      </label>
    ))}
  </div>
);

// Multi-select of staff by name or designation; the dropdown sections the
// matches by designation (the feed arrives in designation-seniority order).
export const StaffPicker = ({ staff, selectedIds, onChange }) => {
  const [q, setQ] = useState('');
  const selected = staff.filter(s => selectedIds.includes(s.id));
  const term = q.trim().toLowerCase();
  const matches = term
    ? staff.filter(s => (s.name.toLowerCase().includes(term)
        || (s.position || '').toLowerCase().includes(term)) && !selectedIds.includes(s.id)).slice(0, 8)
    : [];
  const sections = [];
  const byGroup = new Map();
  matches.forEach(s => {
    const g = s.position || 'No designation';
    if (!byGroup.has(g)) { byGroup.set(g, []); sections.push(g); }
    byGroup.get(g).push(s);
  });

  return (
    <div>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {selected.length === 0 && <span className="text-xs text-slate-500">No recipients selected</span>}
        {selected.map(s => (
          <span key={s.id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-xs">
            {s.name}
            <button onClick={() => onChange(selectedIds.filter(id => id !== s.id))} className="hover:text-blue-900"><X className="w-3 h-3" /></button>
          </span>
        ))}
      </div>
      <div className="relative max-w-sm">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input type="text" value={q} onChange={e => setQ(e.target.value)} placeholder="Search staff by name or designation…" className={`${inputCls} pl-9`} />
        {matches.length > 0 && (
          <div className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg">
            {sections.map(g => (
              <React.Fragment key={g}>
                <div className="px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">{g}</div>
                {byGroup.get(g).map(s => (
                  <button key={s.id} onClick={() => { onChange([...selectedIds, s.id]); setQ(''); }}
                    className="w-full text-left px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50">
                    {s.name}
                  </button>
                ))}
              </React.Fragment>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ── Send times ──────────────────────────────────────────────────────────────

export const pad = (n) => String(n).padStart(2, '0');

// One HH:MM box bound to a {hour, minute} entry.
export const TimeInput = ({ value, onChange, label }) => (
  <div className="w-36">
    <input
      type="time"
      value={`${pad(value.hour)}:${pad(value.minute)}`}
      onChange={e => {
        const [h, m] = e.target.value.split(':').map(Number);
        onChange({ hour: h || 0, minute: m || 0 });
      }}
      className={inputCls}
      aria-label={label}
    />
  </div>
);

export const RemoveTimeButton = ({ onClick, disabled }) => (
  <span className="ml-auto shrink-0 inline-flex">
    <LockTip text={disabled ? 'Keep at least one send time' : null} align="right">
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        title={disabled ? undefined : 'Remove this send time'}
        className="p-1.5 rounded text-slate-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-30 disabled:hover:text-slate-400 disabled:hover:bg-transparent"
      >
        <X className="w-4 h-4" />
      </button>
    </LockTip>
  </span>
);

export const AddTimeButton = ({ onClick }) => (
  <button type="button" onClick={onClick} className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700">
    <Plus className="w-4 h-4" /> Add Another Time
  </button>
);

// The daily send-time list: one row per {hour, minute}. The list never goes
// empty here (the last row cannot be removed); the API merges duplicates on
// save. A new row is one hour after the last, so two clicks never collide.
export const DailyTimesEditor = ({ schedules, onChange }) => {
  const setAt = (i, patch) => onChange(schedules.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  const add = () => {
    const last = schedules[schedules.length - 1];
    onChange([...schedules, last ? { hour: (last.hour + 1) % 24, minute: last.minute } : { hour: 9, minute: 0 }]);
  };
  const remove = (i) => onChange(schedules.filter((_, j) => j !== i));
  return (
    <>
      <div className="space-y-2">
        {schedules.map((s, i) => (
          <div key={i} className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-slate-200 p-2">
            <span className="text-sm text-slate-600">Every day at</span>
            <TimeInput value={s} onChange={(patch) => setAt(i, patch)} label="At" />
            <RemoveTimeButton onClick={() => remove(i)} disabled={schedules.length <= 1} />
          </div>
        ))}
      </div>
      <AddTimeButton onClick={add} />
    </>
  );
};
